import React, { ReactNode } from 'react'
import {
  Container,
  Header,
  Button,
  Box,
  SpaceBetween,
  Icon
} from '@cloudscape-design/components'

interface EmptyState {
  icon: string
  title: string
  description: string
  action?: ReactNode
}

interface RightSidebarProps {
  isExpanded: boolean
  onToggle: () => void
  title: string
  width?: number
  minWidth?: number
  children: ReactNode
  headerActions?: ReactNode
  emptyState?: EmptyState
  viewMode?: 'tree' | 'list'
  collapsedItems?: Array<{
    id: string
    name: string
    type: 'factory' | 'production_line' | 'equipment_component'
    status: string
    onClick: () => void
  }>
}

export const RightSidebar: React.FC<RightSidebarProps> = ({
  isExpanded,
  onToggle,
  title,
  width = 320,
  minWidth = 280,
  children,
  headerActions,
  emptyState,
  viewMode = 'tree',
  collapsedItems = []
}) => {
  const hasContent = React.Children.count(children) > 0
  


  return (
    <div
      style={{
        width: isExpanded ? `${width}px` : '48px',
        minWidth: isExpanded ? `${minWidth}px` : '48px',
        height: '100%',
        borderLeft: '1px solid #e9ebed',
        backgroundColor: isExpanded ? '#fafbfc' : '#f9f9f9',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: isExpanded ? 'none' : '-2px 0 8px rgba(0,0,0,0.1)'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: isExpanded ? '16px' : '12px 8px',
          borderBottom: isExpanded ? '1px solid #e9ebed' : 'none',
          backgroundColor: isExpanded ? 'white' : '#f9f9f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          minHeight: '64px'
        }}
      >
        {isExpanded ? (
          <>
            <Header variant="h3" actions={headerActions}>
              {title}
            </Header>
            <Button
              variant="icon"
              iconName="angle-right"
              onClick={onToggle}
              ariaLabel="Collapse sidebar"
            />
          </>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            backgroundColor: '#0073bb',
            borderRadius: '6px',
            cursor: 'pointer'
          }} onClick={onToggle}>
            <Icon name="view-vertical" size="medium" variant="inverted" />
          </div>
        )}
      </div>

      {/* Collapsed Content - Flattened Asset List */}
      {!isExpanded && collapsedItems.length > 0 && (
        <div 
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '8px 4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
          className="hidden-scrollbar"
        >
          <style>{`
            .hidden-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          {collapsedItems.map((item) => {
            const getIcon = () => {
              if (item.type === 'factory') return 'external'
              if (item.type === 'production_line') return 'settings'
              if (item.type === 'equipment_component') {
                const name = item.name.toLowerCase()
                if (name.includes('extruder')) return 'share'
                if (name.includes('feeder')) return 'upload'
                if (name.includes('motor')) return 'settings-automation'
                if (name.includes('pump')) return 'refresh'
                if (name.includes('tank')) return 'folder'
                if (name.includes('barrel')) return 'view-full'
                if (name.includes('die')) return 'close'
                return 'hardware'
              }
              return 'status-info'
            }
            
            const getColor = () => {
              switch (item.type) {
                case 'factory': return '#1976d2'
                case 'production_line': return '#388e3c'
                case 'equipment_component': return '#616161'
                default: return '#666'
              }
            }
            
            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '6px 2px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  marginBottom: '4px',
                  transition: 'background-color 0.2s'
                }}
                onClick={item.onClick}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e3f2fd'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                title={item.name}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: getColor(),
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '2px'
                }}>
                  <Icon name={getIcon()} size="small" variant="inverted" />
                </div>
                <div style={{
                  fontSize: '8px',
                  color: '#666',
                  textAlign: 'center',
                  lineHeight: '1.1',
                  maxWidth: '32px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {item.name.length > 4 ? item.name.substring(0, 4) + '...' : item.name}
                </div>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: 
                    item.status === 'operational' || item.status === 'running' ? '#4caf50' :
                    item.status === 'idle' ? '#ff9800' :
                    item.status === 'shutdown' ? '#f44336' : '#9e9e9e',
                  marginTop: '2px'
                }} />
              </div>
            )
          })}
        </div>
      )}

      {/* Content */}
      {isExpanded && (
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: hasContent ? '16px' : '0'
          }}
        >
          {hasContent ? (
            children
          ) : emptyState ? (
            <Box textAlign="center" color="text-body-secondary" padding="xl">
              <SpaceBetween size="m">
                <div style={{ fontSize: '32px' }}>{emptyState.icon}</div>
                <div>
                  <Box variant="strong">{emptyState.title}</Box>
                  <Box variant="p" color="text-body-secondary">
                    {emptyState.description}
                  </Box>
                </div>
                {emptyState.action && emptyState.action}
              </SpaceBetween>
            </Box>
          ) : (
            <Box textAlign="center" color="text-body-secondary" padding="xl">
              <SpaceBetween size="m">
                <Icon name="status-info" size="big" />
                <div>No content available</div>
              </SpaceBetween>
            </Box>
          )}
        </div>
      )}
    </div>
  )
}